/**
 * Status Calculator Helpers
 * Determines health status based on latency/time thresholds
 */

import { 
  HealthStatus, 
  DependencyKey, 
  EventKey, 
  DependencyStatus,
  HealthDependencies,
  HealthEvents
} from '../types';
import { DEPENDENCY_THRESHOLDS, EVENT_THRESHOLDS } from '../constants';

/**
 * Calculate dependency status based on connection state and latency
 */
export const calculateDependencyStatus = (
  key: DependencyKey,
  dependency: DependencyStatus
): HealthStatus => {
  // If status is error, always return error
  if (dependency.status === 'error') {
    return 'error';
  }

  const thresholds = DEPENDENCY_THRESHOLDS[key];
  const latency = dependency.latencyMs;

  if (latency < thresholds.healthy) {
    return 'healthy';
  } else if (latency < thresholds.degraded) {
    return 'degraded';
  } else {
    return 'error';
  }
};

/**
 * Calculate event status based on time elapsed since last occurrence
 */
export const calculateEventStatus = (
  key: EventKey,
  timestamp: string | null
): HealthStatus => {
  if (!timestamp) {
    return 'error'; // No data = investigate
  }

  const thresholds = EVENT_THRESHOLDS[key];
  if (!thresholds) {
    return 'healthy'; // Unknown event, assume healthy
  }

  const eventTime = new Date(timestamp).getTime();
  const now = Date.now();
  const elapsed = now - eventTime;

  if (elapsed < thresholds.healthy) {
    return 'healthy';
  } else if (elapsed < thresholds.degraded) {
    return 'degraded';
  } else {
    return 'error';
  }
};

/**
 * Calculate overall system status from all dependencies and events
 */
export const calculateOverallStatus = (
  dependencies: HealthDependencies,
  events: HealthEvents
): HealthStatus => {
  const dependencyStatuses: HealthStatus[] = [
    calculateDependencyStatus('database', dependencies.database),
    calculateDependencyStatus('cache', dependencies.cache),
    calculateDependencyStatus('blobStorage', dependencies.blobStorage),
    calculateDependencyStatus('serviceBus', dependencies.serviceBus)
  ];

  const eventStatuses: HealthStatus[] = Object.entries(events)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => calculateEventStatus(key as EventKey, value as string | null));

  const allStatuses = [...dependencyStatuses, ...eventStatuses];

  // If any dependency has an error, overall is error
  if (dependencyStatuses.includes('error')) {
    return 'error';
  }

  // If any dependency is degraded, overall is degraded
  if (dependencyStatuses.includes('degraded')) {
    return 'degraded';
  }

  // For events, only consider error if multiple are in error state
  const eventErrors = eventStatuses.filter(s => s === 'error').length;
  if (eventErrors >= 2) {
    return 'degraded'; // Multiple events in "investigate" state
  }

  // If any event is degraded (unusual), overall might be degraded
  const eventDegraded = eventStatuses.filter(s => s === 'degraded').length;
  if (eventDegraded >= 3) {
    return 'degraded';
  }

  return 'healthy';
};

/**
 * Get latency percentage relative to threshold (for progress bar)
 * Returns a value between 0 and 100, capped at 100
 */
export const getLatencyPercentage = (
  key: DependencyKey,
  latencyMs: number
): number => {
  const thresholds = DEPENDENCY_THRESHOLDS[key];
  const maxThreshold = thresholds.degraded;
  const percentage = (latencyMs / maxThreshold) * 100;
  return Math.min(percentage, 100);
};

/**
 * Get the color class for a health status
 */
export const getStatusColorClass = (status: HealthStatus): string => {
  switch (status) {
    case 'healthy':
      return 'statusHealthy';
    case 'degraded':
      return 'statusDegraded';
    case 'error':
      return 'statusError';
    default:
      return 'statusLoading';
  }
};
